<template>
  <div class="deal-drawer" :class="{ 'deal-drawer--open': show }">
    <div class="deal-drawer__overlay"></div>

    <div class="deal-drawer__content">
      <!-- Loading State -->
      <div class="deal-drawer__loading" v-if="loading">
        <div class="deal-drawer__spinner"></div>
      </div>

      <template v-else-if="currentDeal">
        <!-- Header -->
        <div class="deal-drawer__header">
          <!-- Customer row: avatar + name/contact + navigation -->
          <div class="deal-drawer__customer">
            <div class="deal-drawer__avatar">{{ initials }}</div>

            <div class="deal-drawer__identity">
              <div class="deal-drawer__name-row">
                <button
                  v-if="customerLeadId"
                  type="button"
                  class="deal-drawer__name deal-drawer__name--link"
                  :title="$t('deals.customer_history.open_card')"
                  @click="handleOpenCustomer"
                >
                  {{ displayName }}
                </button>
                <h2 v-else class="deal-drawer__name">{{ displayName }}</h2>
                <button
                  type="button"
                  class="deal-drawer__star"
                  :class="{ 'deal-drawer__star--active': currentDeal.is_favorite }"
                  :aria-pressed="currentDeal.is_favorite"
                  @click="toggleFavorite"
                >
                  <BaseIcon :name="currentDeal.is_favorite ? 'star-fill' : 'star'" size="16" />
                </button>
                <span v-if="customerTypeLabel" class="deal-drawer__badge">
                  {{ customerTypeLabel }}
                </span>
              </div>

              <div class="deal-drawer__contact" v-if="contactEmail || contactPhone">
                <button
                  v-if="contactEmail"
                  type="button"
                  class="deal-drawer__contact-item deal-drawer__contact-item--link"
                  :title="contactEmail"
                  @click="handleEmail"
                >
                  <BaseIcon name="mail" size="14" />
                  <span class="deal-drawer__contact-text">{{ contactEmail }}</span>
                </button>
                <span class="deal-drawer__contact-item" v-if="contactPhone" :title="contactPhone">
                  <BaseIcon name="phone" size="14" />
                  <span class="deal-drawer__contact-text">{{ contactPhone }}</span>
                </span>
              </div>
            </div>

            <div class="deal-drawer__nav">
              <template v-if="showNavigation">
                <button
                  type="button"
                  class="deal-drawer__icon-btn deal-drawer__nav-arrow"
                  :disabled="!previousDeal"
                  :title="$t('deals.previous_deal')"
                  :aria-label="$t('deals.previous_deal')"
                  @click="navigateToDeal(previousDeal)"
                >
                  <BaseIcon name="chevron" size="16" />
                </button>
                <button
                  type="button"
                  class="deal-drawer__icon-btn deal-drawer__nav-arrow"
                  :disabled="!nextDeal"
                  :title="$t('deals.next_deal')"
                  :aria-label="$t('deals.next_deal')"
                  @click="navigateToDeal(nextDeal)"
                >
                  <BaseIcon name="chevron" size="16" :rotate="180" />
                </button>
              </template>

              <DropdownMenu class="deal-drawer__more">
                <template #trigger>
                  <button
                    type="button"
                    class="deal-drawer__icon-btn"
                    :title="$t('deals.more_actions')"
                    :aria-label="$t('deals.more_actions')"
                  >
                    <BaseIcon name="dots-horizontal" size="16" />
                  </button>
                </template>
                <template #items>
                  <button type="button" class="dropdown-menu__item" @click="handleMerge">
                    <BaseIcon name="merge" size="14" />
                    {{ $t('deals.merge_deals') }}
                  </button>
                  <button
                    type="button"
                    class="dropdown-menu__item dropdown-menu__item--danger"
                    @click="handleDelete"
                  >
                    <BaseIcon name="trash" size="14" />
                    {{ $t('deals.delete_deal') }}
                  </button>
                </template>
              </DropdownMenu>

              <button
                type="button"
                class="deal-drawer__icon-btn"
                :title="$t('common.close')"
                :aria-label="$t('common.close')"
                @click="$emit('close')"
              >
                <BaseIcon name="xmark" size="16" />
              </button>
            </div>
          </div>

          <!-- Key facts -->
          <div class="deal-drawer__facts">
            <div class="deal-drawer__fact">
              <div class="deal-drawer__fact-label">{{ $t('deals.value') }}</div>
              <div class="deal-drawer__fact-value deal-drawer__fact-value--strong">
                {{ formatCurrency(currentDeal.value) }}
              </div>
            </div>

            <div
              class="deal-drawer__fact deal-drawer__fact--stage"
              v-click-outside="closeStageMenu"
            >
              <div class="deal-drawer__fact-label">{{ $t('deals.stage') }}</div>
              <button
                type="button"
                class="deal-drawer__fact-trigger"
                :class="{ 'deal-drawer__fact-trigger--disabled': isCurrentDealClosed }"
                :disabled="isCurrentDealClosed"
                :aria-expanded="stageMenuOpen"
                @click="toggleStageMenu"
              >
                <span
                  class="deal-drawer__stage-dot"
                  :style="{ backgroundColor: currentStageColor }"
                ></span>
                <span class="deal-drawer__fact-value">{{ currentDeal.stage?.name || '-' }}</span>
                <BaseIcon
                  v-if="!isCurrentDealClosed"
                  name="chevron"
                  size="12"
                  :rotate="180"
                  class="deal-drawer__fact-chevron"
                />
              </button>

              <div v-if="stageMenuOpen" class="deal-drawer__stage-menu">
                <button
                  v-for="stage in currentPipelineStages"
                  :key="stage.id"
                  type="button"
                  class="deal-drawer__stage-option"
                  :class="{
                    'deal-drawer__stage-option--active': stage.id === currentDeal.stage_id,
                  }"
                  @click="selectStage(stage.id)"
                >
                  <span
                    class="deal-drawer__stage-dot"
                    :style="{ backgroundColor: stage.color || 'var(--indigo-500)' }"
                  ></span>
                  <span class="deal-drawer__stage-option-name">{{ stage.name }}</span>
                  <BaseIcon v-if="stage.id === currentDeal.stage_id" name="check" size="14" />
                </button>
              </div>
            </div>

            <div class="deal-drawer__fact">
              <div class="deal-drawer__fact-label">{{ $t('deals.in_stage') }}</div>
              <div class="deal-drawer__fact-value deal-drawer__fact-value--muted">
                <BaseIcon name="clock" size="14" />
                {{ $t('deals.days_in_stage', { count: daysInStage }) }}
              </div>
            </div>

            <div class="deal-drawer__fact">
              <div class="deal-drawer__fact-label">{{ $t('deals.source') }}</div>
              <div
                class="deal-drawer__fact-value deal-drawer__fact-value--muted"
                :title="currentDeal.source || $t('deals.unknown_source')"
              >
                <BaseIcon name="filter" size="14" />
                <span class="deal-drawer__fact-text">
                  {{ currentDeal.source || $t('deals.unknown_source') }}
                </span>
              </div>
            </div>

            <div class="deal-drawer__fact deal-drawer__fact--owner">
              <div class="deal-drawer__fact-label">{{ $t('deals.owner') }}</div>
              <DealOwnerPicker
                show-label
                :owner="currentDeal.owner"
                :owner-id="currentOwnerId"
                :members="members"
                @select="handleAssignOwner"
              />
            </div>
          </div>

          <!-- Actions + tags -->
          <div class="deal-drawer__actions">
            <button
              type="button"
              class="deal-drawer__btn deal-drawer__btn--primary"
              :disabled="!contactPhone"
              @click="handleCall"
            >
              <BaseIcon name="phone" size="16" />
              <span>{{ $t('deals.call') }}</span>
            </button>
            <button
              type="button"
              class="deal-drawer__btn"
              :disabled="!contactEmail"
              @click="handleEmail"
            >
              <BaseIcon name="mail" size="16" />
              <span>{{ $t('deals.email') }}</span>
            </button>
            <button type="button" class="deal-drawer__btn" @click="handleAddNote">
              <BaseIcon name="edit" size="16" />
              <span>{{ $t('deals.note') }}</span>
            </button>
            <button type="button" class="deal-drawer__btn" @click="handleAddTask">
              <BaseIcon name="check-circle-open" size="16" />
              <span>{{ $t('deals.task') }}</span>
            </button>

            <div class="deal-drawer__tags">
              <DealTags :deal-id="dealId" :deal="currentDeal" @deal-updated="handleDealUpdated" />
            </div>
          </div>
        </div>

        <!-- Body with Tabs -->
        <div class="deal-drawer__body">
          <Tabs v-model="activeTab" display="default" size="sm" class="deal-drawer__tabs">
            <!-- Activity Tab -->
            <Tab name="activity" :title="$t('deals.tabs.activity')" icon="activity">
              <DealActivities ref="dealActivitiesRef" :deal-id="dealId" />
            </Tab>

            <!-- Tasks Tab -->
            <Tab
              name="tasks"
              :title="$t('deals.tabs.tasks')"
              icon="check-circle-open"
              :badge="currentDeal.tasks_count"
            >
              <DealTasks
                ref="dealTasksRef"
                :deal-id="dealId"
                :initial-task-id="initialTaskId"
                :lead-id="customerLeadId"
                @open-customer="handleOpenCustomer"
              />
            </Tab>

            <Tab name="customer" :title="$t('deals.customer_history.tab')" icon="user">
              <DealCustomerHistory
                v-if="activeTab === 'customer'"
                :lead="currentDeal.lead"
                :deal-id="dealId"
                @open-customer="handleOpenCustomer"
                @open-deal="handleOpenRelatedDeal"
                @email-customer="handleEmail"
              />
            </Tab>

            <!-- Proposals Tab -->
            <Tab
              name="proposals"
              :title="$t('deals.tabs.proposals')"
              icon="file-text"
              :badge="currentDeal.proposals_count"
            >
              <!-- Pro forma accepted banner -->
              <div
                v-if="hasAcceptedProforma && !dealHasProject && !isCurrentDealClosed"
                class="deal-drawer__proforma-banner"
              >
                <div class="deal-drawer__proforma-banner-text">
                  <BaseIcon name="check-circle" size="16" />
                  <div>
                    <strong>{{ $t('proposals.proforma_accepted_banner') }}</strong>
                    <p>{{ $t('proposals.proforma_accepted_description') }}</p>
                  </div>
                </div>
                <div class="deal-drawer__proforma-banner-actions">
                  <Btn
                    variant="primary"
                    size="sm"
                    icon="clipboard"
                    @click="handleCreateProjectWithMeasurement"
                  >
                    {{ $t('proposals.create_project_and_measurement') }}
                  </Btn>
                </div>
              </div>
              <DealProposals :deal-id="dealId" :deal="currentDeal" />
            </Tab>

            <Tab name="documents" :title="$t('deals.tabs.documents')" icon="file">
              <EntityDocuments owner-type="deal" :owner-id="dealId" />
            </Tab>

            <!-- Project Tab -->
            <Tab name="project" :title="$t('deals.tabs.project')" icon="clipboard">
              <div v-if="openSalesOrder" class="deal-drawer__sales-order">
                <span>{{ $t('projects.sales_order') }}</span>
                <button type="button" @click="showSalesOrder(openSalesOrder.id)">
                  {{ openSalesOrder.sales_order_number }}
                </button>
              </div>
              <DealJourneyBar
                class="deal-drawer__journey"
                :deal-id="dealId"
                :refresh-key="journeyRefreshKey"
                @navigate="handleJourneyNavigate"
                @deal-action="handleJourneyDealAction"
                @open-sales-order="showSalesOrder"
              />
              <DealProject
                ref="dealProjectRef"
                :deal-id="dealId"
                :deal="currentDeal"
                :empty-description="
                  canCreateManualSalesOrder ? $t('sales_orders.create_project_hint') : ''
                "
                :create-label="canCreateManualSalesOrder ? $t('sales_orders.create') : ''"
                @create-project="handleCreateProject"
                @navigate-to-project="handleNavigateToProject"
                @reopen-project="handleReopenProject"
              />
            </Tab>

            <!-- Settings/Info Tab -->
            <Tab name="settings" :title="$t('deals.tabs.settings')" icon="sliders">
              <DealSettings
                :deal-id="dealId"
                :deal="currentDeal"
                @deal-updated="handleDealUpdated"
                @delete="handleDelete"
              />
            </Tab>
          </Tabs>
        </div>

        <!-- Footer -->
        <div class="deal-drawer__footer" v-if="!isCurrentDealClosed">
          <button
            v-if="nextStage"
            type="button"
            class="deal-drawer__btn deal-drawer__next-stage"
            @click="handleStageChange(nextStage.id)"
          >
            <span>{{ $t('deals.move_to_next_stage', { stage: nextStage.name }) }}</span>
            <BaseIcon name="arrow-right" size="16" />
          </button>

          <button
            type="button"
            class="deal-drawer__btn deal-drawer__stage-mobile"
            @click="showStageDrawer = true"
          >
            <span>{{ $t('deals.move_to_stage') }}</span>
            <BaseIcon name="chevron" size="16" />
          </button>

          <BottomDrawer
            v-model="showStageDrawer"
            :title="$t('deals.choose_stage')"
            :items="stageDrawerItems"
            :active-value="currentDeal?.stage_id"
            :current-value="currentDeal?.stage_id"
            :current-label="$t('deals.current')"
            @select="handleStageChange"
          />

          <div class="deal-drawer__footer-actions">
            <button
              type="button"
              class="deal-drawer__btn deal-drawer__btn--danger"
              @click="handleMarkLost"
            >
              <BaseIcon name="xmark" size="16" />
              <span>{{ $t('deals.lost') }}</span>
            </button>
            <button
              type="button"
              class="deal-drawer__btn deal-drawer__btn--success"
              @click="handleMarkWon"
            >
              <BaseIcon name="check" size="16" />
              <span>{{ $t('deals.won') }}</span>
            </button>
          </div>
        </div>

        <!-- Closed Deal Footer -->
        <div class="deal-drawer__footer deal-drawer__footer--closed" v-else>
          <div class="deal-drawer__footer-actions">
            <button type="button" class="deal-drawer__btn" @click="handleReopen">
              <BaseIcon name="redo" size="16" />
              <span>{{ $t('deals.reopen') }}</span>
            </button>
            <button
              v-if="isCurrentDealWon && !dealHasProject"
              type="button"
              class="deal-drawer__btn deal-drawer__btn--primary"
              @click="handleCreateProject"
            >
              <BaseIcon name="clipboard" size="16" />
              <span>{{ $t('deals.create_project') }}</span>
            </button>
            <button
              v-if="isCurrentDealWon && dealHasProject"
              type="button"
              class="deal-drawer__btn deal-drawer__btn--primary"
              @click="handleNavigateToProject(dealProjectId)"
            >
              <BaseIcon name="clipboard" size="16" />
              <span>{{ $t('projects.view_project') }}</span>
            </button>
          </div>
        </div>
      </template>

      <!-- Empty State -->
      <div v-else class="deal-drawer__empty">
        <BaseIcon name="briefcase" size="48" />
        <h3>{{ $t('deals.no_deal_selected') }}</h3>
        <p>{{ $t('deals.select_deal_to_view') }}</p>
      </div>

      <SalesOrderDrawer
        :show="salesOrderDrawerOpen"
        :sales-order-id="salesOrderDrawerId"
        @close="salesOrderDrawerOpen = false"
        @cancelled="refreshDealSalesOrder"
      />
    </div>
  </div>
</template>

<script>
  import {
    defineComponent,
    ref,
    computed,
    getCurrentInstance,
    watch,
    nextTick,
    onBeforeUnmount,
  } from 'vue';
  import { useRouter } from 'vue-router';
  import { useDealStore } from '@/store/dealStore';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import { useUserStore } from '@/store/userStore';
  import { useCustomerEmail } from '@/composables/useCustomerEmail';
  import { useConfetti } from '@/services/confettiService';
  import { useProjects } from '@/composables/useProjects';
  import { useDealDelete } from '@/composables/useDealDelete';
  import { projectService } from '@/services/projectService';
  import { dealService } from '@/services/dealService';
  import { crmService } from '@/services/crmService';
  import OrganizationService from '@/services/organizationService';

  import Tabs from '@/components/partials/Tabs/Tabs.vue';
  import Tab from '@/components/partials/Tabs/Tab.vue';
  import DealActivities from '@/components/deals/DealActivities.vue';
  import DealTasks from '@/components/deals/DealTasks.vue';
  import DealCustomerHistory from '@/components/deals/DealCustomerHistory.vue';
  import CreateTaskModal from '@/components/tasks/CreateTaskModal.vue';
  import DealProposals from '@/components/deals/DealProposals.vue';
  import EntityDocuments from '@/components/documents/EntityDocuments.vue';
  import DealProject from '@/components/deals/DealProject.vue';
  import DealSettings from '@/components/deals/DealSettings.vue';
  import DealTags from '@/components/deals/DealTags.vue';
  import DealJourneyBar from '@/components/deals/DealJourneyBar.vue';
  import SalesOrderDrawer from '@/components/orders/SalesOrderDrawer.vue';
  import SalesOrderWizard from '@/components/orders/SalesOrderWizard.vue';
  import { salesOrderService } from '@/services/salesOrderService';
  import DealOwnerPicker from '@/components/deals/DealOwnerPicker.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@/components/partials/Btn.vue';
  import DropdownMenu from '@/components/partials/DropdownMenu.vue';
  import BottomDrawer from '@/components/partials/BottomDrawer.vue';

  const DEFAULT_STAGE_COLOR = '#6366f1';

  export default defineComponent({
    name: 'DealDetailDrawer',

    components: {
      Tabs,
      Tab,
      DealActivities,
      DealTasks,
      DealCustomerHistory,
      DealProposals,
      EntityDocuments,
      DealProject,
      DealSettings,
      DealTags,
      DealJourneyBar,
      SalesOrderDrawer,
      DealOwnerPicker,
      BaseIcon,
      Btn,
      DropdownMenu,
      BottomDrawer,
    },

    props: {
      show: {
        type: Boolean,
        default: false,
      },
      dealId: {
        type: String,
        default: null,
      },
      initialTab: {
        type: String,
        default: 'activity',
      },
      initialTaskId: {
        type: String,
        default: null,
      },
      // Show previous/next deal buttons (order follows the loaded pipeline board)
      showNavigation: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['close', 'deal-updated', 'deal-deleted', 'navigate-deal'],

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const router = useRouter();
      const dealStore = useDealStore();
      const uiStore = useUIStore();
      const toastStore = useToastStore();
      const userStore = useUserStore();
      const { composeEmail } = useCustomerEmail();
      const confetti = useConfetti();

      const activeTab = ref(props.initialTab);

      const currentDeal = computed(() => dealStore.currentDeal);
      const loading = computed(() => dealStore.currentDealLoading);
      const isCurrentDealWon = computed(() => dealStore.isCurrentDealWon);
      const isCurrentDealLost = computed(() => dealStore.isCurrentDealLost);
      const isCurrentDealClosed = computed(() => dealStore.isCurrentDealClosed);

      const dealProjectRef = ref(null);
      const dealProject = ref(null);
      const dealHasProject = computed(() => !!dealProject.value);
      const dealProjectId = computed(() => dealProject.value?.id);

      const canCreateManualSalesOrder = computed(() => {
        if (!userStore.salesOrdersEnabled) return false;
        if (dealHasProject.value || isCurrentDealLost.value || openSalesOrder.value) return false;
        const proposals = dealStore.dealProposals || [];
        return !proposals.some((proposal) =>
          ['draft', 'sent', 'viewed', 'sending', 'accepted'].includes(proposal.status)
        );
      });

      const hasAcceptedProforma = computed(() => {
        const proposals = dealStore.dealProposals || [];
        return proposals.some((p) => p.status === 'accepted' && p.proposal_type === 'proforma');
      });

      const openSalesOrder = ref(null);
      const salesOrderDrawerOpen = ref(false);
      const salesOrderDrawerId = ref(null);

      const refreshDealSalesOrder = async () => {
        if (!props.dealId) {
          openSalesOrder.value = null;
          return;
        }
        try {
          const rows = await salesOrderService.list({ dealId: props.dealId });
          openSalesOrder.value = rows.find((row) => row.status !== 'cancelled') || null;
        } catch (error) {
          openSalesOrder.value = null;
        }
        await refreshDealProject();
        await dealStore.fetchDeal(props.dealId);
      };

      const showSalesOrder = (id) => {
        salesOrderDrawerId.value = id;
        salesOrderDrawerOpen.value = true;
      };

      const handleCreateSalesOrder = () => {
        const modalId = uiStore.addModal(SalesOrderWizard, {
          title: $t('sales_orders.create'),
          size: 'xxl',
          bodyScroll: false,
          deal: currentDeal.value,
          onSubmit: async () => {
            uiStore.closeModal(modalId);
            await refreshDealSalesOrder();
            toastStore.addToast({ type: 'success', message: $t('sales_orders.create') });
          },
        });
      };

      const refreshDealProject = async () => {
        try {
          dealProject.value = await projectService.getProjectByDealId(props.dealId);
          dealProjectRef.value?.refresh?.();
        } catch (e) {
          // Non-critical
        }
      };

      const currentPipelineStages = computed(() => {
        const pipeline = dealStore.currentPipeline;
        return pipeline?.stages || [];
      });

      const currentStageColor = computed(
        () => currentDeal.value?.stage?.color || DEFAULT_STAGE_COLOR
      );

      // Next regular stage after the current one (won/lost stages are set via the footer buttons)
      const nextStage = computed(() => {
        const stages = currentPipelineStages.value;
        const index = stages.findIndex((stage) => stage.id === currentDeal.value?.stage_id);
        if (index === -1) return null;
        return stages.slice(index + 1).find((stage) => !stage.is_won && !stage.is_lost) || null;
      });

      const showStageDrawer = ref(false);
      const stageMenuOpen = ref(false);

      const stageDrawerItems = computed(() => {
        return currentPipelineStages.value.map((stage) => ({
          label: stage.name,
          value: stage.id,
          color: stage.color || DEFAULT_STAGE_COLOR,
        }));
      });

      const closeStageMenu = () => {
        stageMenuOpen.value = false;
      };

      const toggleStageMenu = () => {
        // Below the lg breakpoint ($break-lg) the bottom drawer is the better picker
        if (window.matchMedia('(max-width: 992px)').matches) {
          showStageDrawer.value = true;
          return;
        }
        stageMenuOpen.value = !stageMenuOpen.value;
      };

      const selectStage = (stageId) => {
        closeStageMenu();
        if (stageId === currentDeal.value?.stage_id) return;
        handleStageChange(stageId);
      };

      // Previous / next deal, following the order of the loaded board
      const orderedDeals = computed(() =>
        dealStore.kanbanStages.flatMap((stage) => stage.deals || [])
      );
      const currentDealIndex = computed(() =>
        orderedDeals.value.findIndex((deal) => deal.id === props.dealId)
      );
      const previousDeal = computed(() =>
        currentDealIndex.value > 0 ? orderedDeals.value[currentDealIndex.value - 1] : null
      );
      const nextDeal = computed(() =>
        currentDealIndex.value === -1
          ? null
          : orderedDeals.value[currentDealIndex.value + 1] || null
      );

      const navigateToDeal = (deal) => {
        if (!deal) return;
        closeStageMenu();
        emit('navigate-deal', deal);
      };

      // Owner
      const organizationService = new OrganizationService();
      const members = ref([]);
      const currentOwnerId = computed(
        () => currentDeal.value?.owner_id || currentDeal.value?.owner?.id || null
      );

      const loadMembers = async () => {
        const organizationId = userStore.currentOrganization?.id;
        if (!organizationId || members.value.length) return;
        try {
          members.value = await organizationService.getMembers(organizationId);
        } catch (error) {
          members.value = [];
        }
      };

      const handleAssignOwner = async (ownerId) => {
        try {
          await dealStore.updateDeal(props.dealId, { owner_id: ownerId });
          emit('deal-updated', dealStore.currentDeal);
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('deals.update_failed'),
          });
        }
      };

      // Customer type badge (lead-based deals only)
      const customerTypeLabel = computed(() => {
        const lead = currentDeal.value?.lead;
        if (!lead) return null;
        return lead.customer_type === 'company'
          ? $t('lead_details.customer_type.company')
          : $t('lead_details.customer_type.person');
      });

      // Display name: contact/lead name or deal title
      const displayName = computed(() => {
        // Try lead name first
        if (currentDeal.value?.lead) {
          const lead = currentDeal.value.lead;
          if (lead.first_name || lead.last_name) {
            return [lead.first_name, lead.last_name].filter(Boolean).join(' ');
          }
          if (lead.name) return lead.name;
        }
        // Try contact name
        if (currentDeal.value?.contact) {
          const contact = currentDeal.value.contact;
          if (contact.first_name || contact.last_name) {
            return [contact.first_name, contact.last_name].filter(Boolean).join(' ');
          }
          if (contact.name) return contact.name;
        }
        // Fallback to deal title
        return currentDeal.value?.title || $t('deals.untitled_deal');
      });

      const initials = computed(() => {
        const parts = String(displayName.value || '')
          .trim()
          .split(/\s+/)
          .filter(Boolean);
        if (!parts.length) return '?';
        return parts
          .slice(0, 2)
          .map((part) => part.charAt(0))
          .join('')
          .toUpperCase();
      });

      // Contact info
      const contactPhone = computed(() => {
        return (
          currentDeal.value?.lead?.phone ||
          currentDeal.value?.contact?.phone ||
          currentDeal.value?.contact?.phone_number
        );
      });

      const contactEmail = computed(() => {
        return currentDeal.value?.lead?.email || currentDeal.value?.contact?.email;
      });

      // Days in current stage (falls back to deal age when the stage timestamp is missing)
      const daysInStage = computed(() => {
        const deal = currentDeal.value;
        if (!deal) return 0;
        if (typeof deal.days_in_stage === 'number') return deal.days_in_stage;
        const since = deal.stage_entered_at || deal.created_at;
        if (!since) return 0;
        const diffTime = Date.now() - new Date(since).getTime();
        return Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)));
      });

      const formatCurrency = (value) => {
        return new Intl.NumberFormat('nl-NL', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value || 0);
      };

      // Fetch deal and all related data when dealId changes
      watch(
        () => props.dealId,
        async (newId) => {
          closeStageMenu();
          if (!newId) {
            dealProject.value = null;
            openSalesOrder.value = null;
            return;
          }
          // Load deal and all related data in parallel
          await Promise.all([
            loadMembers(),
            dealStore.fetchDeal(newId),
            dealStore.fetchDealActivities(newId),
            dealStore.fetchDealTasks(newId),
            dealStore.fetchDealSubmissions(newId),
            dealStore.fetchDealProposals(newId),
            projectService
              .getProjectByDealId(newId)
              .then((p) => {
                dealProject.value = p;
              })
              .catch(() => {}),
            salesOrderService
              .list({ dealId: newId })
              .then((rows) => {
                openSalesOrder.value = rows.find((row) => row.status !== 'cancelled') || null;
              })
              .catch(() => {
                openSalesOrder.value = null;
              }),
          ]);
        },
        { immediate: true }
      );

      // Escape closes the drawer (or the innermost open layer first).
      // Modals register their own Escape handler, so we stay out of the way
      // while one is open.
      const handleEscape = (event) => {
        if (event.key !== 'Escape' || event.defaultPrevented) return;
        if (uiStore.modals.length > 0) return;

        if (stageMenuOpen.value || showStageDrawer.value) {
          event.preventDefault();
          stageMenuOpen.value = false;
          showStageDrawer.value = false;
          return;
        }

        const active = document.activeElement;
        if (
          active &&
          (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)
        ) {
          active.blur();
          return;
        }

        event.preventDefault();
        emit('close');
      };

      watch(
        () => props.show,
        (isOpen) => {
          if (isOpen) {
            document.addEventListener('keydown', handleEscape);
          } else {
            document.removeEventListener('keydown', handleEscape);
          }
        },
        { immediate: true }
      );

      onBeforeUnmount(() => {
        document.removeEventListener('keydown', handleEscape);
      });

      // Update activeTab when initialTab prop changes
      watch(
        () => props.initialTab,
        (newTab) => {
          if (newTab) {
            activeTab.value = newTab;
          }
        }
      );

      watch(
        () => props.initialTaskId,
        (newTaskId) => {
          if (newTaskId) {
            activeTab.value = 'tasks';
          }
        },
        { immediate: true }
      );

      const dealActivitiesRef = ref(null);
      const dealTasksRef = ref(null);

      const handleAddNote = async () => {
        activeTab.value = 'activity';
        await nextTick();
        dealActivitiesRef.value?.focusInput?.();
      };

      const handleAddTask = () => {
        activeTab.value = 'tasks';
        const modalId = uiStore.addModal(CreateTaskModal, {
          title: $t('deals.new_task'),
          size: 'md',
          context: 'deal',
          dealId: props.dealId,
          onSubmit: async (task) => {
            uiStore.closeModal(modalId);
            await nextTick();
            dealTasksRef.value?.addCreatedTask?.(task);
          },
        });
      };

      const handleMerge = () => {
        import('@/components/deals/MergeDealsModal.vue').then((module) => {
          const MergeDealsModal = module.default;
          const modalId = uiStore.addModal(MergeDealsModal, {
            title: $t('deals.merge_deals'),
            size: 'md',
            dealId: props.dealId,
            onSubmit: async () => {
              uiStore.closeModal(modalId);
              await dealStore.fetchDeal(props.dealId);
              emit('deal-updated', dealStore.currentDeal);
            },
            onCancel: () => {
              uiStore.closeModal(modalId);
            },
          });
        });
      };

      const { deleteDeal } = useDealDelete({
        onDeleted: () => emit('deal-deleted'),
        onOpenProject: (projectId) => handleNavigateToProject(projectId),
      });

      const handleDelete = () => deleteDeal(props.dealId, dealProject.value);

      const toggleFavorite = async () => {
        try {
          await dealStore.toggleFavorite(props.dealId, !currentDeal.value?.is_favorite);
          toastStore.addToast({
            type: 'success',
            message: currentDeal.value?.is_favorite
              ? $t('deals.removed_from_favorites')
              : $t('deals.added_to_favorites'),
          });
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('deals.favorite_failed'),
          });
        }
      };

      const handleCall = () => {
        if (contactPhone.value) {
          window.location.href = `tel:${contactPhone.value}`;
        }
      };

      const handleEmail = () => {
        if (!contactEmail.value) return;
        composeEmail({
          dealId: props.dealId,
          email: contactEmail.value,
          onSent: () => dealStore.fetchDealActivities(props.dealId),
        });
      };

      const handleStageChange = async (stageId) => {
        try {
          await dealStore.moveDealToStage(props.dealId, stageId, currentDeal.value?.stage_id);
          await dealStore.fetchDeal(props.dealId);
          toastStore.addToast({
            type: 'success',
            message: $t('deals.stage_updated'),
          });
          emit('deal-updated');
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('deals.stage_update_failed'),
          });
        }
      };

      const executeMarkWon = async (acceptProposalIds = []) => {
        try {
          for (const { leadId, proposalId } of acceptProposalIds) {
            await crmService.markProposalAsAccepted(leadId, proposalId);
          }
          await dealStore.markDealAsWon(props.dealId);
          confetti.fire();
          toastStore.addToast({
            type: 'success',
            message: $t('deals.marked_as_won'),
          });
          emit('deal-updated');
          await refreshDealProject();
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('deals.mark_won_failed'),
          });
        }
      };

      const handleMarkWon = async () => {
        try {
          const { proposals } = await dealService.getDealProposals(props.dealId);
          const acceptableStatuses = ['sent', 'viewed', 'rejected'];
          const nonAccepted = (proposals || []).filter((p) =>
            acceptableStatuses.includes(p.status)
          );

          if (nonAccepted.length > 0) {
            const modalId = uiStore.addModal(false, {
              title: $t('deals.mark_won_confirm_title'),
              description: $t('deals.mark_won_confirm_proposals', {
                count: nonAccepted.length,
              }),
              submitText: $t('deals.mark_won_accept_proposals'),
              cancelText: $t('deals.mark_won_skip_proposals'),
              onSubmit: async () => {
                uiStore.closeModal(modalId);
                const toAccept = nonAccepted.map((p) => ({
                  leadId: p.lead_id || p.lead?.id,
                  proposalId: p.id,
                }));
                await executeMarkWon(toAccept);
              },
              onCancel: async () => {
                uiStore.closeModal(modalId);
                await executeMarkWon();
              },
            });
          } else {
            await executeMarkWon();
          }
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('deals.mark_won_failed'),
          });
        }
      };

      const handleMarkLost = async () => {
        const reason = prompt($t('deals.enter_lost_reason'));

        try {
          await dealStore.markDealAsLost(props.dealId, reason);
          toastStore.addToast({
            type: 'info',
            message: $t('deals.marked_as_lost'),
          });
          emit('deal-updated');
          await refreshDealProject();
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('deals.mark_lost_failed'),
          });
        }
      };

      const handleReopen = async () => {
        try {
          await dealStore.reopenDeal(props.dealId);
          toastStore.addToast({
            type: 'success',
            message: $t('deals.deal_reopened'),
          });
          emit('deal-updated');
          await refreshDealProject();
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('deals.reopen_failed'),
          });
        }
      };

      const handleCreateProject = () => {
        if (canCreateManualSalesOrder.value) {
          handleCreateSalesOrder();
          return;
        }
        import('@/components/projects/CreateProjectModal.vue').then((module) => {
          const CreateProjectModal = module.default;
          const modalId = uiStore.addModal(CreateProjectModal, {
            title: $t('projects.create_from_deal'),
            size: 'xl',
            deal: currentDeal.value,
            onSubmit: async (project) => {
              uiStore.closeModal(modalId);
              if (project?.id) {
                handleNavigateToProject(project.id);
              }
            },
          });
        });
      };

      const handleCreateProjectWithMeasurement = () => {
        import('@/components/projects/CreateProjectModal.vue').then((module) => {
          const CreateProjectModal = module.default;
          const modalId = uiStore.addModal(CreateProjectModal, {
            title: $t('projects.create_from_deal'),
            size: 'xl',
            deal: currentDeal.value,
            createMeasurementWorkOrder: true,
            onSubmit: async (project) => {
              uiStore.closeModal(modalId);
              if (project?.id) {
                handleNavigateToProject(project.id);
              }
            },
          });
        });
      };

      const handleNavigateToProject = (projectId) => {
        if (!projectId) return;
        router.push({ name: 'ProjectDetail', params: { id: projectId } });
        emit('close', { skipRouteReset: true });
      };

      const handleReopenProject = async (projectId) => {
        try {
          const { reopenProject } = useProjects();
          const project = await reopenProject(projectId);
          if (project) dealProject.value = project;
          await dealProjectRef.value?.refresh?.();
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('projects.reopen_failed') || 'Failed to reopen project',
          });
        }
      };

      const handleDealUpdated = (updatedDeal) => {
        emit('deal-updated', updatedDeal);
      };

      const journeyRefreshKey = computed(() => {
        const deal = currentDeal.value;
        const proposals = (dealStore.dealProposals || []).map((p) => `${p.id}:${p.status}`);
        return [
          deal?.stage_id,
          deal?.updated_at,
          dealProject.value?.id,
          dealProject.value?.status,
          ...proposals,
        ].join('|');
      });

      const handleJourneyNavigate = (route) => {
        router.push(route);
        emit('close', { skipRouteReset: true });
      };

      const customerLeadId = computed(
        () => currentDeal.value?.lead?.id || currentDeal.value?.lead_id || null
      );

      const handleOpenCustomer = () => {
        if (!customerLeadId.value) return;
        handleJourneyNavigate({ name: 'LeadDetail', params: { id: customerLeadId.value } });
      };

      const handleOpenRelatedDeal = (deal) => {
        if (instance.vnode.props?.onNavigateDeal) {
          navigateToDeal(deal);
          return;
        }
        handleJourneyNavigate({ name: 'DealDetail', params: { id: deal.id } });
      };

      const handleJourneyDealAction = (step) => {
        if (step.key === 'start_project') {
          handleCreateProject();
        } else {
          activeTab.value = 'proposals';
        }
      };

      return {
        activeTab,
        currentDeal,
        loading,
        isCurrentDealWon,
        isCurrentDealLost,
        isCurrentDealClosed,
        currentPipelineStages,
        currentStageColor,
        nextStage,
        showStageDrawer,
        stageMenuOpen,
        stageDrawerItems,
        closeStageMenu,
        toggleStageMenu,
        selectStage,
        previousDeal,
        nextDeal,
        navigateToDeal,
        members,
        currentOwnerId,
        handleAssignOwner,
        customerTypeLabel,
        displayName,
        initials,
        contactPhone,
        contactEmail,
        daysInStage,
        formatCurrency,
        dealActivitiesRef,
        dealTasksRef,
        handleAddNote,
        handleAddTask,
        handleMerge,
        handleDelete,
        toggleFavorite,
        handleCall,
        handleEmail,
        handleStageChange,
        handleMarkWon,
        handleMarkLost,
        handleReopen,
        handleCreateProject,
        handleCreateProjectWithMeasurement,
        handleNavigateToProject,
        handleReopenProject,
        handleDealUpdated,
        journeyRefreshKey,
        handleJourneyNavigate,
        handleJourneyDealAction,
        customerLeadId,
        handleOpenCustomer,
        handleOpenRelatedDeal,
        openSalesOrder,
        salesOrderDrawerOpen,
        salesOrderDrawerId,
        showSalesOrder,
        handleCreateSalesOrder,
        canCreateManualSalesOrder,
        refreshDealSalesOrder,
        dealProjectRef,
        dealHasProject,
        dealProjectId,
        hasAcceptedProforma,
      };
    },
  });
</script>

<style scoped lang="scss">
  .deal-drawer {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;

    &--open {
      pointer-events: all;
      opacity: 1;
    }

    &__overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      cursor: pointer;
    }

    &__content {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: 90vw;
      max-width: 1000px;
      background: white;
      box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
      display: flex;
      flex-direction: column;
      transform: translateX(100%);
      transition: transform 0.3s ease;

      @media (max-width: 768px) {
        width: 100vw;
        max-width: none;
      }
    }

    &--open &__content {
      transform: translateX(0);
    }

    // Loading
    &__loading {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1;
    }

    &__spinner {
      width: 32px;
      height: 32px;
      border: 3px solid var(--slate-200);
      border-top-color: var(--brand-500);
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    // Header
    &__header {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      flex-shrink: 0;
      background: white;
      padding: 1.5rem 1.5rem 0;

      @include lg-down {
        gap: 0.75rem;
        padding: calc(0.75rem + env(safe-area-inset-top, 0px)) 0.75rem 0;
      }
    }

    // Customer row
    &__customer {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
    }

    &__avatar {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      flex-shrink: 0;
      border-radius: 50%;
      background: var(--slate-200);
      color: var(--slate-900);
      font-size: 13px;
      font-weight: 500;
      text-transform: uppercase;
    }

    &__identity {
      display: flex;
      flex: 1;
      flex-direction: column;
      gap: 0.5rem;
      min-width: 0;
    }

    &__name-row {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.25rem 0.5rem;
      min-width: 0;
      min-height: 24px;
    }

    &__name {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 500;
      line-height: 1.2;
      color: var(--slate-900);
      @include ellipsis(1);

      &--link {
        padding: 0;
        border: 0;
        background: transparent;
        font-family: inherit;
        text-align: left;
        cursor: pointer;

        @include hover {
          color: #3364e6;
          text-decoration: underline;
          text-underline-offset: 3px;
        }
      }
    }

    &__star {
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
      background: none;
      border: none;
      padding: 0.125rem;
      cursor: pointer;
      color: var(--slate-400);
      transition: color 0.2s ease;

      @include hover {
        color: var(--amber-400);
      }

      &--active {
        color: var(--amber-500);
      }
    }

    &__badge {
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
      height: 18px;
      padding: 0 6px;
      border-radius: 6px;
      background: var(--slate-200);
      color: var(--slate-600);
      font-size: 10px;
      font-weight: 500;
      line-height: 1;
      text-transform: uppercase;
      letter-spacing: 0.02em;
    }

    &__contact {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.25rem 0.75rem;
    }

    &__contact-item {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      max-width: 100%;
      min-width: 0;
      color: var(--slate-600);
      font-size: 13px;
      line-height: 20px;

      .icon {
        color: var(--slate-500);
      }

      &--link {
        padding: 0;
        border: 0;
        background: transparent;
        font-family: inherit;
        cursor: pointer;

        @include hover {
          color: #3364e6;
          text-decoration: underline;
          text-underline-offset: 3px;
        }
      }
    }

    &__contact-text {
      min-width: 0;
      @include ellipsis(1);
    }

    &__nav {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-shrink: 0;

      @include lg-down {
        gap: 0.375rem;
      }
    }

    &__icon-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      padding: 0;
      border: 1px solid var(--slate-300);
      border-radius: 4px;
      background: white;
      color: var(--slate-700);
      cursor: pointer;
      transition:
        background-color 0.15s ease,
        border-color 0.15s ease;

      @include hover {
        &:not(:disabled) {
          background: var(--slate-50);
          border-color: var(--slate-400);
        }
      }

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }

      &:focus-visible {
        outline: 2px solid var(--brand-500);
        outline-offset: 2px;
      }

      // Previous/next deal: desktop only, the header is too narrow on small screens
      &.deal-drawer__nav-arrow {
        @include lg-down {
          display: none;
        }
      }
    }

    &__more {
      :deep(.dropdown-menu__item) {
        background: none;
        border: 0;
        font-size: 13px;
        color: var(--slate-700);
        white-space: nowrap;
      }

      :deep(.dropdown-menu__item--danger) {
        color: var(--red-600);
      }
    }

    // Key facts
    &__facts {
      display: flex;
      align-items: stretch;
      border: 1px solid var(--slate-200);
      border-radius: 4px;
      background: white;

      @include lg-down {
        flex-wrap: wrap;
      }
    }

    &__fact {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      min-width: 0;
      padding: 0.75rem 1rem;
      border-left: 1px solid var(--slate-200);

      &:first-child {
        border-left: 0;
      }

      &--stage {
        flex: 1;
      }

      @include lg-down {
        flex: 1 1 45%;
        padding: 0.625rem 0.75rem;

        &:nth-child(odd) {
          border-left: 0;
        }

        &:nth-child(n + 3) {
          border-top: 1px solid var(--slate-200);
        }
      }
    }

    &__fact-label {
      color: var(--slate-500);
      font-size: 10px;
      font-weight: 500;
      line-height: 1;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      white-space: nowrap;
    }

    &__fact-value {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      min-width: 0;
      color: var(--slate-900);
      font-size: 13px;
      font-weight: 500;
      line-height: 20px;
      white-space: nowrap;

      &--strong {
        font-weight: 600;
      }

      &--muted {
        color: var(--slate-600);
        font-weight: 400;

        .icon {
          color: var(--slate-500);
        }
      }
    }

    &__fact-text {
      @include ellipsis(1);
    }

    &__fact-trigger {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      align-self: flex-start;
      max-width: 100%;
      margin: -2px -4px;
      padding: 2px 4px;
      border: 0;
      border-radius: 4px;
      background: transparent;
      cursor: pointer;
      text-align: left;

      .deal-drawer__fact-value {
        @include ellipsis(1);
      }

      @include hover {
        &:not(:disabled) {
          background: var(--slate-100);
        }
      }

      &[aria-expanded='true'] {
        background: var(--slate-100);
      }

      &:disabled {
        cursor: default;
      }

      &:focus-visible {
        outline: 2px solid var(--brand-500);
        outline-offset: 2px;
      }
    }

    &__fact-chevron {
      color: var(--slate-500);
    }

    &__stage-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    &__stage-menu {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      z-index: 30;
      display: flex;
      flex-direction: column;
      min-width: 220px;
      max-height: 320px;
      overflow-y: auto;
      padding: 6px;
      background: white;
      border: 1px solid var(--slate-200);
      border-radius: 4px;
      box-shadow: 0 8px 12px rgba(0, 0, 0, 0.12);
    }

    &__stage-option {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      width: 100%;
      padding: 8px;
      border: 0;
      border-radius: 4px;
      background: transparent;
      color: var(--slate-900);
      font-size: 13px;
      line-height: 20px;
      text-align: left;
      cursor: pointer;

      @include hover {
        background: var(--slate-100);
      }

      &--active {
        font-weight: 500;

        .icon {
          color: var(--brand-600);
        }
      }
    }

    &__stage-option-name {
      flex: 1;
      @include ellipsis(1);
    }

    &__sales-order {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      margin-bottom: 0.75rem;
      font-size: 0.875rem;

      span {
        color: var(--slate-500);
      }

      button {
        padding: 0;
        border: 0;
        background: none;
        color: var(--brand-600);
        font: inherit;
        cursor: pointer;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    &__journey {
      margin-bottom: 1rem;
    }

    // Actions + tags
    &__actions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.5rem;
    }

    // Design buttons: 36px, 4px radius, 13px medium, 16px icon
    &__btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.375rem;
      height: 36px;
      padding: 0 0.75rem;
      background: white;
      border: 1px solid var(--slate-300);
      border-radius: 4px;
      font-family: inherit;
      font-size: 13px;
      font-weight: 500;
      line-height: 20px;
      color: var(--slate-700);
      white-space: nowrap;
      cursor: pointer;
      transition:
        background-color 0.15s ease,
        border-color 0.15s ease,
        color 0.15s ease,
        opacity 0.15s ease;

      @include hover {
        &:hover:not(:disabled) {
          background: var(--slate-50);
          border-color: var(--slate-400);
          color: var(--slate-900);
        }
      }

      &:focus-visible {
        outline: 2px solid var(--brand-300);
        outline-offset: 1px;
      }

      &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }

      &--primary {
        background: var(--brand-600);
        border-color: var(--brand-600);
        color: white;

        @include hover {
          &:hover:not(:disabled) {
            background: var(--brand-700);
            border-color: var(--brand-700);
            color: white;
          }
        }
      }

      &--success {
        background: var(--green-600);
        border-color: var(--green-600);
        color: white;

        @include hover {
          &:hover:not(:disabled) {
            background: var(--green-700);
            border-color: var(--green-700);
            color: white;
          }
        }
      }

      &--danger {
        color: var(--red-600);

        @include hover {
          &:hover:not(:disabled) {
            background: var(--red-50);
            border-color: var(--red-100);
            color: var(--red-600);
          }
        }
      }
    }

    &__tags {
      display: flex;
      flex: 1;
      justify-content: flex-end;
      min-width: 0;

      @include lg-down {
        flex-basis: 100%;
        justify-content: flex-start;
      }
    }

    // Body
    &__body {
      display: flex;
      flex: 1;
      flex-direction: column;
      min-height: 0;
      min-width: 0;
      overflow: hidden;
      background: var(--slate-100);
    }

    &__tabs {
      flex: 1;
      min-height: 0;

      :deep(.tabs__headers) {
        flex-shrink: 0;
        gap: 1.5rem;
        padding: 0.5rem 1.5rem 0;
        background: white;
        border-bottom: 1px solid var(--slate-200);
        overflow-x: auto;
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;

        &::-webkit-scrollbar {
          display: none;
        }

        @include lg-down {
          gap: 1rem;
          padding: 0.25rem 0.75rem 0;
        }
      }

      :deep(.tabs__header) {
        flex-shrink: 0;
        gap: 0.375rem;
        padding: 0.75rem 0;
        margin-bottom: -1px;
        color: var(--slate-600);
        font-size: 13px;
        font-weight: 400;
        line-height: 20px;
        white-space: nowrap;
      }

      :deep(.tabs__header--active) {
        color: var(--slate-900);
        font-weight: 500;
        border-bottom-color: var(--brand-500);
      }

      :deep(.tabs__badge) {
        min-width: 0;
        height: 18px;
        padding: 0 6px;
        border-radius: 6px;
        background: var(--slate-200);
        color: var(--slate-600);
        font-size: 10px;
        font-weight: 500;
      }

      :deep(.tabs__content) {
        flex: 1;
        min-height: 0;
        overflow-x: hidden;
        overflow-y: auto;
        padding: 1.5rem;

        @include lg-down {
          padding: 0.75rem 0.5rem 1rem;
        }
      }
    }

    // Footer
    &__footer {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding: 1rem 1.5rem;
      background: white;
      border-top: 1px solid var(--slate-200);
      @include lg-down {
        padding: 0.5rem;
        padding-bottom: calc(0.5rem + env(safe-area-inset-bottom, 0px));
      }
      &--closed {
        flex-direction: column;
        gap: 0.75rem;
        justify-content: flex-end;
        .deal-drawer__footer-actions {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          width: 100%;
          gap: 0.5rem;
        }
      }
    }

    &__next-stage {
      @include lg-down {
        display: none;
      }
    }

    &__stage-mobile {
      display: none;
      @include lg-down {
        display: inline-flex;
        width: 100%;
        justify-content: space-between;
      }
    }

    &__footer-actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-left: auto;
      @include lg-down {
        display: none;
      }
    }

    // Empty State
    &__empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      flex: 1;
      padding: 4rem 2rem;
      color: var(--slate-500);

      .icon {
        color: var(--slate-300);
        margin-bottom: 1rem;
      }

      h3 {
        font-size: 1.125rem;
        font-weight: 600;
        color: var(--slate-900);
        margin: 0 0 0.5rem 0;
      }

      p {
        font-size: 0.875rem;
        margin: 0;
      }
    }
    &__proforma-banner {
      margin-bottom: 1rem;
      padding: 0.75rem 1rem;
      background: var(--green-50);
      border: 1px solid var(--green-200);
      border-radius: 0.5rem;
    }

    &__proforma-banner-text {
      display: flex;
      gap: 0.5rem;
      color: var(--green-700);

      strong {
        display: block;
        font-size: 0.8125rem;
        margin-bottom: 0.125rem;
      }

      p {
        font-size: 0.75rem;
        color: var(--green-600);
        margin: 0;
        line-height: 1.4;
      }
    }

    &__proforma-banner-actions {
      margin-top: 0.625rem;
      display: flex;
      gap: 0.5rem;
    }
  }
</style>
